import { type ComponentProps, type JSX, useState } from "react";

import useIntegrationSettings from "common/datahooks/useIntegrationSettings";
import { typedObjectEntries } from "common/helpers/utils";
import {
  ContentContainer,
  ContentHeader,
  PageTitle,
} from "common/layouts/WorkspaceLayout";
import type { IntegrationSettings } from "common/types";

import { INTEGRATIONS_MAP } from "../constants";
import CrmIntegrationSettings from "./CrmIntegrationSettings";
import EmailOutreachConnect from "./EmailOutreachConnect";
import EmailOutreachSettings from "./EmailOutreachSettings";
import IntegrationItem from "./IntegrationItem";
import Rb2bIntegrationSettings from "./Rb2bIntegrationSettings";
import RemoveIntegrationDialog from "./RemoveIntegrationDialog";

export default function IntegrationsList() {
  const { integrationSettings } = useIntegrationSettings();
  const [removingIntegrationKey, setRemovingIntegrationKey] = useState<
    keyof IntegrationSettings | null
  >(null);
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);

  return (
    <>
      <ContentContainer>
        <ContentHeader>
          <PageTitle />
        </ContentHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 p-4">
          {typedObjectEntries(INTEGRATIONS_MAP).map(
            ([key, { icon, title, description, ...rest }]) => {
              const commonIntegrationItemProps = {
                icon,
                type: key,
                description,
              };
              let integrationItemProps:
                | { redirectUrl: string }
                | { settingsComponent: JSX.Element; hasError?: boolean };

              if ("redirectUrl" in rest) {
                integrationItemProps = { redirectUrl: rest.redirectUrl };
              } else {
                if (!integrationSettings) {
                  return (
                    <IntegrationItem
                      key={key}
                      {...commonIntegrationItemProps}
                      isLoading
                    />
                  );
                }

                if (key === "instantly" || key === "smartlead") {
                  let settingsComponent = <EmailOutreachConnect type={key} />;
                  let errorMessage: string;

                  const onRemove = () => {
                    setRemovingIntegrationKey(key);
                    setIsRemoveDialogOpen(true);
                  };

                  if (integrationSettings[key]) {
                    const settings = integrationSettings[key];
                    let settingsProps: ComponentProps<
                      typeof EmailOutreachSettings
                    >;

                    if ("error" in settings) {
                      errorMessage = settings.error;
                      settingsProps = {
                        type: "instantly",
                        errorMessage,
                        onRemove,
                      };
                    } else if ("workspace_name" in settings) {
                      settingsProps = {
                        type: "instantly",
                        workspaceName: settings.workspace_name,
                        onRemove,
                      };
                    } else {
                      settingsProps = {
                        type: "smartlead",
                        apiKey: settings.api_key,
                        onRemove,
                      };
                    }

                    settingsComponent = (
                      <EmailOutreachSettings key={key} {...settingsProps} />
                    );
                  }

                  integrationItemProps = {
                    settingsComponent,
                    hasError: !!errorMessage,
                  };
                } else if (key === "hubspot" || key === "pipedrive") {
                  const settings = integrationSettings[key];
                  integrationItemProps =
                    "install_url" in settings
                      ? { redirectUrl: settings.install_url }
                      : {
                          settingsComponent: (
                            <CrmIntegrationSettings
                              {...("error" in settings
                                ? { errorMessage: settings.error }
                                : { settings: settings })}
                              crmType={key}
                              onRemove={() => {
                                setRemovingIntegrationKey(key);
                                setIsRemoveDialogOpen(true);
                              }}
                            />
                          ),
                        };
                } else if (key === "rb2b") {
                  integrationItemProps = {
                    settingsComponent: (
                      <Rb2bIntegrationSettings
                        settings={integrationSettings[key]}
                        onRemove={() => {
                          setRemovingIntegrationKey(key);
                          setIsRemoveDialogOpen(true);
                        }}
                      />
                    ),
                  };
                }
              }

              return (
                <IntegrationItem
                  key={key}
                  {...commonIntegrationItemProps}
                  {...integrationItemProps}
                />
              );
            },
          )}
        </div>
      </ContentContainer>
      <RemoveIntegrationDialog
        type={removingIntegrationKey}
        isOpen={isRemoveDialogOpen}
        setIsOpen={(isOpen) => {
          setIsRemoveDialogOpen(isOpen);
        }}
      />
    </>
  );
}
